import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Truck, Car, Cog, ChevronDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls = "bg-zinc-900 border border-zinc-800 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-shadow duration-150 w-full";
const VICON = { commercial: Truck, passenger: Car, custom: Cog };

function AddCustomer({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [vehicles, setVehicles] = useState([{ type: "passenger", year: "", make: "", model: "", plate: "" }]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updV = (i, k, v) => setVehicles(vehicles.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/customers", { ...form, vehicles });
      toast.success("Customer added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto p-6" data-testid="customer-modal">
      <div className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-display text-xl font-bold uppercase">New Customer</h2>
          <button onClick={onClose}><X className="text-zinc-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Name" value={form.name} onChange={set("name")} className={inputCls} data-testid="customer-name" />
            <input placeholder="Company (optional)" value={form.company} onChange={set("company")} className={inputCls} data-testid="customer-company" />
            <input placeholder="Email" type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="customer-email" />
            <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="customer-phone" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase text-zinc-400 font-bold">Vehicles</span>
              <button onClick={() => setVehicles([...vehicles, { type: "passenger", year: "", make: "", model: "", plate: "" }])}
                className="text-xs font-bold uppercase text-yellow-500 flex items-center gap-1" data-testid="add-vehicle"><Plus size={14} /> Add</button>
            </div>
            {vehicles.map((v, i) => (
              <div key={i} className="grid grid-cols-5 gap-2 mb-2">
                <select value={v.type} onChange={(e) => updV(i, "type", e.target.value)} className={inputCls}>
                  <option value="commercial">Commercial</option><option value="passenger">Passenger</option><option value="custom">Custom</option>
                </select>
                <input placeholder="Year" value={v.year} onChange={(e) => updV(i, "year", e.target.value)} className={inputCls} />
                <input placeholder="Make" value={v.make} onChange={(e) => updV(i, "make", e.target.value)} className={inputCls} />
                <input placeholder="Model" value={v.model} onChange={(e) => updV(i, "model", e.target.value)} className={inputCls} />
                <input placeholder="Plate" value={v.plate} onChange={(e) => updV(i, "plate", e.target.value)} className={inputCls} />
              </div>
            ))}
          </div>
          <button onClick={save} disabled={saving} data-testid="customer-save"
            className="w-full bg-yellow-500 text-black font-bold uppercase py-3 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
            {saving ? "Saving…" : "Save Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/customers").then(({ data }) => setCustomers(data));
  useEffect(load, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tighter">Customers</h1>
          <p className="text-zinc-500 text-sm">Directory · multiple vehicles per account</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-customer-btn"
          className="bg-yellow-500 text-black font-bold uppercase px-5 py-3 flex items-center gap-2 hover:bg-yellow-300 transition-colors duration-150">
          <Plus size={18} /> New Customer
        </button>
      </div>

      <div className="space-y-3">
        {customers.map((c) => (
          <div key={c.id} className="border border-zinc-800 bg-zinc-950" data-testid={`customer-row-${c.email}`}>
            <button onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-900/50 transition-colors duration-150">
              <div>
                <p className="font-display text-lg font-bold">{c.name} {c.company && <span className="text-zinc-500 text-sm font-normal">· {c.company}</span>}</p>
                <p className="text-zinc-400 text-sm">{c.email} · {c.phone}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs uppercase text-zinc-500">{c.vehicles.length} vehicle{c.vehicles.length !== 1 ? "s" : ""}</span>
                <ChevronDown className={`text-zinc-500 transition-transform duration-150 ${expanded === c.id ? "rotate-180" : ""}`} size={18} />
              </div>
            </button>
            {expanded === c.id && (
              <div className="border-t border-zinc-800 p-5 grid sm:grid-cols-2 gap-3">
                {c.vehicles.map((v) => {
                  const Icon = VICON[v.type] || Car;
                  return (
                    <div key={v.id} className="flex items-center gap-3 border border-zinc-800 p-3">
                      <Icon className="text-yellow-500" size={20} />
                      <div>
                        <p className="text-sm font-semibold">{v.year} {v.make} {v.model}</p>
                        <p className="text-xs text-zinc-500 uppercase">{v.type} · {v.plate}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {show && <AddCustomer onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}
